if (!customElements.get('macfox-product-customize')) {
  customElements.define('macfox-product-customize', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      const viewport = this.querySelector('[data-customize-viewport]');
      const track = this.querySelector('[data-customize-track]');
      const slides = Array.from(this.querySelectorAll('[data-customize-slide]'));
      const dots = Array.from(this.querySelectorAll('[data-customize-dot]'));
      const previous = this.querySelector('[data-customize-prev]');
      const next = this.querySelector('[data-customize-next]');
      const status = this.querySelector('[data-customize-status]');
      if (!viewport || !track || !slides.length) return;

      this.abort = new AbortController();
      this.autoplayFrame = 0;
      this.scrollFrame = 0;
      this.cloneSlides = [];
      const options = { signal: this.abort.signal };
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const hoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      const designMode = this.dataset.customizeDesignMode === 'true';
      const autoplayEnabled = this.dataset.customizeAutoplay === 'true' && slides.length > 1;
      const parsedSpeed = Number.parseFloat(this.dataset.customizeAutoplaySpeed);
      const autoplaySpeed = Number.isFinite(parsedSpeed) ? Math.max(20, Math.min(80, parsedSpeed)) : 50;
      const parsedMobileSpeed = Number.parseFloat(this.dataset.customizeMobileAutoplaySpeed);
      const mobileAutoplaySpeed = Number.isFinite(parsedMobileSpeed) ? Math.max(20, Math.min(120, parsedMobileSpeed)) : 80;
      let cloneSlides = [];
      let index = -1;
      let pointer = null;
      let interactionPointerId = null;
      let inView = false;
      let interacting = false;
      let hovering = false;
      let previousTimestamp = null;
      let autoplayLeft = null;

      const slideLeft = slide => slide.offsetLeft - slides[0].offsetLeft;
      const reachableLeft = slide => Math.min(
        slideLeft(slide),
        Math.max(0, viewport.scrollWidth - viewport.clientWidth)
      );
      const cycleWidth = () => {
        if (!cloneSlides.length) return 0;
        return Math.max(0, cloneSlides[0].offsetLeft - slides[0].offsetLeft);
      };
      const normalizeLeft = (left, width = cycleWidth()) => {
        if (!width) return Math.max(0, left);
        return ((left % width) + width) % width;
      };
      const syncAutoplayLeft = (width = cycleWidth()) => {
        autoplayLeft = normalizeLeft(viewport.scrollLeft, width);
      };
      const wrapToOriginals = () => {
        const width = cycleWidth();
        if (!width) {
          syncAutoplayLeft(width);
          return;
        }
        const normalized = normalizeLeft(viewport.scrollLeft, width);
        autoplayLeft = normalized;
        if (Math.abs(normalized - viewport.scrollLeft) > 0.5) viewport.scrollLeft = normalized;
      };
      const addClones = () => {
        const group = slides.map(slide => {
          const clone = slide.cloneNode(true);
          clone.removeAttribute('data-customize-slide');
          clone.removeAttribute('data-block-id');
          clone.removeAttribute('data-metaobject-handle');
          clone.removeAttribute('data-shopify-editor-block');
          clone.removeAttribute('id');
          clone.setAttribute('data-customize-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          track.appendChild(clone);
          return clone;
        });
        cloneSlides.push(...group);
        this.cloneSlides = cloneSlides;
      };
      const removeClones = () => {
        wrapToOriginals();
        cloneSlides.forEach(clone => clone.remove());
        cloneSlides = [];
        this.cloneSlides = [];
      };
      const syncClones = () => {
        if (!autoplayEnabled || designMode || reducedMotion.matches || viewport.clientWidth <= 0) {
          removeClones();
          return;
        }
        if (!cloneSlides.length) addClones();
        const width = cycleWidth();
        if (!width) {
          removeClones();
          return;
        }
        const lastSlide = slides[slides.length - 1];
        const trailingGap = Math.max(0, width - slideLeft(lastSlide) - lastSlide.offsetWidth);
        const requiredGroups = Math.max(1, Math.ceil((viewport.clientWidth + trailingGap) / width));
        if (cloneSlides.length !== requiredGroups * slides.length) {
          removeClones();
          for (let groupIndex = 0; groupIndex < requiredGroups; groupIndex += 1) addClones();
        }
      };
      const canRunContinuous = () => autoplayEnabled
        && cloneSlides.length >= slides.length
        && cycleWidth() > 0
        && viewport.clientWidth > 0
        && viewport.scrollWidth - viewport.clientWidth >= cycleWidth() - 1
        && !designMode
        && !reducedMotion.matches;
      const canAutoplay = () => canRunContinuous()
        && !document.hidden
        && inView
        && !interacting
        && !hovering;
      const stopAutoplay = (preservePosition = false) => {
        if (this.autoplayFrame) cancelAnimationFrame(this.autoplayFrame);
        this.autoplayFrame = 0;
        previousTimestamp = null;
        if ((hovering || preservePosition) && canRunContinuous()) viewport.classList.add('is-continuous');
        else viewport.classList.remove('is-continuous');
      };
      const update = (value, announce = true, forceAnnouncement = false) => {
        const nextIndex = Math.max(0, Math.min(slides.length - 1, value));
        const changed = nextIndex !== index;
        index = nextIndex;
        if (changed) {
          dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));
          if (previous) previous.disabled = slides.length < 2;
          if (next) next.disabled = slides.length < 2;
        }
        if (status && announce && (changed || forceAnnouncement)) status.textContent = (index + 1) + ' / ' + slides.length;
      };
      const nearest = (announce = false) => {
        const width = cycleWidth();
        const currentLeft = normalizeLeft(viewport.scrollLeft, width);
        let nearestIndex = 0;
        let distance = Infinity;
        slides.forEach((slide, slideIndex) => {
          const targetLeft = width ? slideLeft(slide) : reachableLeft(slide);
          const directDistance = Math.abs(targetLeft - currentLeft);
          const candidate = width ? Math.min(directDistance, Math.max(0, width - directDistance)) : directDistance;
          if (candidate < distance) {
            distance = candidate;
            nearestIndex = slideIndex;
          }
        });
        update(nearestIndex, announce);
      };
      const goTo = (value, behavior = reducedMotion.matches ? 'auto' : 'smooth', announce = true) => {
        const target = ((value % slides.length) + slides.length) % slides.length;
        const left = cloneSlides.length ? slideLeft(slides[target]) : reachableLeft(slides[target]);
        viewport.scrollTo({ left, behavior });
        update(target, announce, announce);
      };
      const autoplayStep = timestamp => {
        this.autoplayFrame = 0;
        if (!canAutoplay()) {
          stopAutoplay();
          return;
        }
        const width = cycleWidth();
        if (width && previousTimestamp !== null) {
          const elapsed = Math.min(64, Math.max(0, timestamp - previousTimestamp));
          const speed = viewport.clientWidth <= 767 ? mobileAutoplaySpeed : autoplaySpeed;
          if (autoplayLeft === null) syncAutoplayLeft(width);
          const actualLeft = normalizeLeft(viewport.scrollLeft, width);
          const directDifference = Math.abs(actualLeft - autoplayLeft);
          if (directDifference > 1.5) autoplayLeft = actualLeft;
          autoplayLeft = normalizeLeft(autoplayLeft + speed * elapsed / 1000, width);
          viewport.scrollLeft = autoplayLeft;
          nearest(false);
        }
        previousTimestamp = timestamp;
        this.autoplayFrame = requestAnimationFrame(autoplayStep);
      };
      const startAutoplay = () => {
        stopAutoplay(true);
        syncClones();
        if (!canAutoplay()) {
          stopAutoplay();
          return;
        }
        syncAutoplayLeft();
        viewport.classList.add('is-continuous');
        this.autoplayFrame = requestAnimationFrame(autoplayStep);
      };
      const manualGoTo = value => {
        stopAutoplay();
        goTo(value, 'auto');
        startAutoplay();
      };

      previous?.addEventListener('click', () => manualGoTo(index - 1), options);
      next?.addEventListener('click', () => manualGoTo(index + 1), options);
      dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => manualGoTo(dotIndex), options));
      viewport.addEventListener('scroll', () => {
        if (viewport.classList.contains('is-continuous')) return;
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          nearest(false);
        });
      }, { ...options, passive: true });
      viewport.addEventListener('dragstart', event => event.preventDefault(), options);
      viewport.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || !hoverPointer.matches) return;
        hovering = true;
        stopAutoplay();
      }, options);
      viewport.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        hovering = false;
        startAutoplay();
      }, options);
      viewport.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          manualGoTo(index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          manualGoTo(index + 1);
        }
      }, options);
      viewport.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        interacting = true;
        interactionPointerId = event.pointerId;
        stopAutoplay();
        if (event.pointerType === 'touch') return;
        if (event.target instanceof Element && event.target.closest('button,a,input,textarea,select')) return;
        pointer = { id: event.pointerId, x: event.clientX, scroll: viewport.scrollLeft, dragged: false };
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture?.(event.pointerId);
      }, options);
      viewport.addEventListener('pointermove', event => {
        if (!pointer || event.pointerId !== pointer.id) return;
        const distance = event.clientX - pointer.x;
        if (!pointer.dragged && Math.abs(distance) < 5) return;
        pointer.dragged = true;
        viewport.scrollLeft = pointer.scroll - distance;
      }, options);
      const endPointer = event => {
        if (interactionPointerId !== null && event.pointerId !== interactionPointerId) return;
        if (!interacting && !pointer) return;
        interacting = false;
        interactionPointerId = null;
        if (!pointer || event.pointerId !== pointer.id) {
          startAutoplay();
          return;
        }
        const dragged = pointer.dragged;
        const pointerId = pointer.id;
        pointer = null;
        viewport.classList.remove('is-dragging');
        if (viewport.hasPointerCapture?.(pointerId)) viewport.releasePointerCapture(pointerId);
        nearest(false);
        goTo(index, 'auto', true);
        if (dragged) {
          const blockClick = click => {
            click.preventDefault();
            click.stopPropagation();
          };
          viewport.addEventListener('click', blockClick, { capture: true, once: true });
          window.setTimeout(() => viewport.removeEventListener('click', blockClick, true), 250);
        }
        startAutoplay();
      };
      window.addEventListener('pointerup', endPointer, options);
      window.addEventListener('pointercancel', endPointer, options);
      viewport.addEventListener('lostpointercapture', endPointer, options);
      document.addEventListener('visibilitychange', startAutoplay, options);
      reducedMotion.addEventListener('change', startAutoplay, options);
      hoverPointer.addEventListener('change', () => {
        if (!hoverPointer.matches) hovering = false;
        startAutoplay();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (event.detail?.sectionId && event.detail.sectionId !== this.dataset.sectionId) return;
        const selected = event.detail?.blockId
          ? slides.find(slide => slide.dataset.blockId === event.detail.blockId)
          : event.target instanceof Element ? event.target.closest('[data-customize-slide]') : null;
        if (!selected || !this.contains(selected)) return;
        manualGoTo(slides.indexOf(selected));
      }, options);

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          startAutoplay();
        });
      }, { threshold: [0, 0.35] });
      this.observer.observe(this);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          wrapToOriginals();
          nearest(false);
          startAutoplay();
        });
        this.resizeObserver.observe(viewport);
      }
      syncClones();
      update(0, false);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.autoplayFrame) cancelAnimationFrame(this.autoplayFrame);
      this.autoplayFrame = 0;
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      this.cloneSlides?.forEach(clone => clone.remove());
      this.cloneSlides = [];
      this.querySelector('[data-customize-viewport]')?.classList.remove('is-continuous', 'is-dragging');
      this.observer?.disconnect();
      this.observer = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }
  });
}
